import React, { useEffect } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Filters from './components/Filters';
import {
  fetchAnalytics,
  fetchCampaigns,
  setFilters,
} from '../../redux/features/magicLampSlice';

/**
 * Magic Lamp — analytics
 *
 * Every figure is counted from the spin log, so nothing here can disagree with
 * what actually happened, and cancelling a spin corrects the whole screen.
 *
 * Filters by campaign and date range.
 */
function StatCard({ label, value, hint, color = 'text.primary' }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h4" fontWeight={800} color={color}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Spins per day, drawn as bars.
 *
 * Deliberately plain SVG rather than a charting library: it is one series of
 * counts, and the portal already carries three different chart packages.
 */
function DailyChart({ rows }) {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
        No spins in this range.
      </Typography>
    );
  }

  const max = Math.max(...rows.map((r) => r.spins), 1);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 180, overflowX: 'auto', pt: 2 }}>
      {rows.map((row) => {
        const height = Math.round((row.spins / max) * 140);
        const winHeight = Math.round((row.wins / max) * 140);
        return (
          <Box key={row.day} sx={{ textAlign: 'center', minWidth: 34 }}>
            <Typography variant="caption" fontWeight={700}>
              {row.spins}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                height: `${height}px`,
                minHeight: 4,
                bgcolor: '#e9d5ff',
                borderRadius: 1,
                mt: 0.5,
              }}
              title={`${row.spins} spins, ${row.wins} wins`}
            >
              {/* Wins shaded inside the same bar, so the two are compared
                  directly rather than across two charts. */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: `${winHeight}px`,
                  bgcolor: '#8f1cab',
                  borderRadius: 1,
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {String(row.day).slice(5)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function Analytics() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Filters live in the slice so they carry across to the spin log — the two
  // screens are views of the same data, and re-picking a date range after
  // switching between them is needless work.
  const {
    campaigns,
    filters,
    analytics: data,
    loadingAnalytics: loading,
  } = useSelector((state) => state.magicLamp);

  useEffect(() => {
    if (!campaigns.length) dispatch(fetchCampaigns());
  }, [dispatch, campaigns.length]);

  useEffect(() => {
    dispatch(fetchAnalytics(filters))
      .unwrap()
      .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
    // Only the fields analytics actually uses; the log's `result` filter must
    // not trigger a refetch here.
  }, [dispatch, filters.campaignId, filters.from, filters.to, enqueueSnackbar]);

  return (
    <GlobalWrapper title="Lucky Spin analytics">
      <Typography variant="body2" color="text.secondary" mb={2}>
        Counted from the spin log. Cancelled spins are left out of every figure
        except their own count.
      </Typography>

      <Filters
        campaigns={campaigns}
        filters={filters}
        onChange={(next) => dispatch(setFilters(next))}
      />

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}>
              <StatCard label="Spins" value={data.totals.spins} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Prizes won" value={data.totals.wins} color="success.main" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Win rate" value={`${data.totals.winRate}%`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Busiest average"
                value={data.totals.averagePerDay}
                hint={`over ${data.totals.activeDays} day(s)`}
              />
            </Grid>
          </Grid>

          {data.totals.voided > 0 && (
            <Chip
              sx={{ mb: 2 }}
              color="warning"
              label={`${data.totals.voided} cancelled spin(s), excluded from the figures above`}
            />
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="overline" color="text.secondary">
                Spins per day
              </Typography>
              <Stack direction="row" gap={2}>
                <Stack direction="row" gap={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, bgcolor: '#e9d5ff', borderRadius: 0.5 }} />
                  <Typography variant="caption">spins</Typography>
                </Stack>
                <Stack direction="row" gap={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, bgcolor: '#8f1cab', borderRadius: 0.5 }} />
                  <Typography variant="caption">wins</Typography>
                </Stack>
              </Stack>
            </Stack>
            <DailyChart rows={data.byDay} />
          </Paper>

          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="overline" color="text.secondary">
                By prize
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                A prize coming up far more often than its set chance usually means a
                mistyped number.
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Prize</TableCell>
                  <TableCell width={100}>Times</TableCell>
                  <TableCell width={140}>Share of spins</TableCell>
                  <TableCell width={120}>Set chance</TableCell>
                  <TableCell width={110}>On the wheel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.bySegment.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nothing spun in this range.
                    </TableCell>
                  </TableRow>
                )}
                {data.bySegment.map((row) => (
                  <TableRow key={row.segmentId || row.label}>
                    <TableCell>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Typography fontWeight={600}>{row.label}</Typography>
                        {!row.isWin && <Chip size="small" label="no win" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.times}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, row.actualChance)}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" fontWeight={700}>
                          {row.actualChance}%
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.configuredChance === null ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        `${row.configuredChance}%`
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isActive === null ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : row.isActive ? (
                        <Chip size="small" color="success" label="on" />
                      ) : (
                        <Chip size="small" label="off" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

    </GlobalWrapper>
  );
}